import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, Shield, Cloud, BrainCircuit, Blocks, MonitorPlay, CheckCircle2, Award, Users, Code } from 'lucide-react';
import PageHeader from '../../components/PageHeader/PageHeader';
import './Training.css';

// Reusable Scroll Reveal Component
const RevealOnScroll = ({ children, className = '', delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (let i = 0; i < entries.length; i++) {
          setIsVisible(entries[i].isIntersecting);
        }
      },
      { threshold: 0.1 }
    );
    const current = domRef.current;
    if (current) observer.observe(current);
    return () => { if (current) observer.unobserve(current); };
  }, []);

  return (
    <div ref={domRef} className={`reveal-wrapper ${isVisible ? 'is-visible' : ''} ${className}`} style={{ transitionDelay: `${delay}s` }}>
      {children}
    </div>
  );
};

export default function Training() {
  const courses = [
    { title: 'Software Development', icon: <Code size={32} />, desc: 'Learn to build modern, scalable web and mobile applications using industry-standard frameworks.' },
    { title: 'Cybersecurity', icon: <Shield size={32} />, desc: 'Master network defense, risk assessments, firewall implementation, and compliance standards.' },
    { title: 'Cloud Computing', icon: <Cloud size={32} />, desc: 'Gain expertise in cloud migration, management, and optimization across major cloud platforms.' },
    { title: 'AI & Machine Learning', icon: <BrainCircuit size={32} />, desc: 'Understand data-driven automation, predictive analytics, and enterprise AI implementation.' },
    { title: 'Blockchain Technologies', icon: <Blocks size={32} />, desc: 'Explore decentralized ledgers, smart contracts, and secure blockchain infrastructure.' }
  ];

  return (
    <div className="training-page">
      <PageHeader 
        title={<>MICT SETA-Accredited <br/><span>Training Programs</span></>} 
        description="Bridging the digital skills gap. Empowering individuals and businesses through SAQA-aligned, industry-leading tech education."
      />

      <div className="training-main-content">
        <div className="container">
          
          <div className="training-intro">
            <RevealOnScroll>
              <span className="section-label">OUR ACADEMY</span>
              <h2>Empowering the Digital Economy</h2>
              <p className="lead-text">
                Singularity Tech offers MICT SETA-accredited training designed to address South Africa's critical 73% skills gap in emerging technologies.
              </p>
              <p className="lead-text">
                We cater to corporate employees seeking upskilling, educational institutions requiring IT curriculums, and individual learners looking to advance their careers. Our flexible delivery models ensure that education is accessible and impactful.
              </p>
            </RevealOnScroll>
          </div>

          <div className="delivery-models-grid">
            <RevealOnScroll delay={0.1} className="delivery-card">
              <MonitorPlay className="delivery-icon" size={40} />
              <h3>Hybrid Delivery</h3>
              <p>Learn through a mix of in-person practicals and our highly scalable online Learning Management System (LMS).</p>
            </RevealOnScroll>
            <RevealOnScroll delay={0.2} className="delivery-card">
              <Award className="delivery-icon" size={40} />
              <h3>SETA-Funded & Aligned</h3>
              <p>Our programs are fully SAQA-aligned, ensuring professional credibility and access to SETA funding.</p>
            </RevealOnScroll>
            <RevealOnScroll delay={0.3} className="delivery-card">
              <Users className="delivery-icon" size={40} />
              <h3>Corporate & SME Focus</h3>
              <p>Customized cohort training packages for SMEs and Corporates investing in AI, IoT, and cybersecurity.</p>
            </RevealOnScroll>
          </div>

          <div className="courses-section">
            <RevealOnScroll>
              <span className="section-label">COURSE CATALOG</span>
              <h2 className="section-title">Specialized IT Courses</h2>
            </RevealOnScroll>
            
            <div className="courses-grid">
              {courses.map((course, idx) => (
                <RevealOnScroll key={idx} delay={idx * 0.1}>
                  <div className="course-card">
                    <div className="course-icon-wrap">{course.icon}</div>
                    <h3>{course.title}</h3>
                    <p>{course.desc}</p>
                    <div className="course-footer">
                      <span className="course-price">Est. R4,000 - R5,000 / Course</span>
                    </div>
                  </div>
                </RevealOnScroll>
              ))}
            </div>
          </div>

          <RevealOnScroll>
            <div className="cta-box training-cta">
              <BookOpen size={48} style={{ color: 'var(--brand-green)', marginBottom: '20px', position: 'relative', zIndex: 2 }} />
              <h3>Ready to Upgrade Your Skills?</h3>
              <p>Whether you are an individual learner or a corporate entity looking to upskill your workforce, our admissions team is ready to assist you.</p>
              <a href="mailto:info@singularitytech.co.za?subject=Training Program Inquiry" className="btn-primary" style={{ textDecoration: 'none', display: 'inline-block' }}>
                Inquire About Enrollment
              </a>
            </div>
          </RevealOnScroll>

        </div>
      </div>
    </div>
  );
}